import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Heart, Leaf, Users, Phone, Mail, MapPin, Calendar, ArrowRight, Sparkles, Shield, BookOpen, MessageCircle, Brain, Target, Bed, Apple, Flower2, Home, Globe, Zap, Coffee, Activity } from 'lucide-react';
import NavBar from '@/components/NavBar';
import { ReturnToTop } from '@/components/ReturnToTop';
import ScrollDownArrow from '@/components/ScrollDownArrow';
import { BOOKING_URL } from '@/constants/urls';
import heroImage from '@/assets/hero_image.jpg';

const DigestionGutHealthPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = "Digestion & Gut Health Currimundi | Chinese Medicine";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Natural digestion and gut health support with Chinese Medicine in Currimundi. Treat bloating, IBS, food sensitivities & digestive issues. Book online or visit our Sunshine Coast clinic.");
    }
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Digestion & Gut Health Currimundi | Chinese Medicine");
    }
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Natural digestion and gut health support with Chinese Medicine in Currimundi. Treat bloating, IBS, food sensitivities & digestive issues. Book online or visit our Sunshine Coast clinic.");
    }
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/digestion-gut-health");
    }
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', "https://womenandtheway.com.au/hero_image.jpg");
    }
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Digestion & Gut Health Currimundi | Chinese Medicine");
    }
    const schema = {
      "@context": "https://schema.org",
      "@type": "MedicalClinic",
      "name": "Women and The Way - Digestion & Gut Health with Chinese Medicine",
      "image": "https://womenandtheway.com.au/hero_image.jpg",
      "description": "Natural digestion and gut health support with Chinese Medicine in Currimundi. Treat bloating, IBS, food sensitivities & digestive issues.",
      "url": "https://womenandtheway.com.au/digestion-gut-health",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "700 Nicklin Way",
        "addressLocality": "Currimundi",
        "addressRegion": "QLD",
        "postalCode": "4551",
        "addressCountry": "AU"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -26.5270,
        "longitude": 153.0890
      },
      "priceRange": "$$",
      "telephone": "0415537784",
      "email": "womenandtheway@gmail.com",
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "17:00"
      },
      "serviceType": "Digestion Support, Gut Health, IBS Treatment, Chinese Medicine, Acupuncture, Herbal Medicine",
      "areaServed": {
        "@type": "Place",
        "name": "Sunshine Coast, Queensland, Australia"
      },
      "sameAs": [
        "https://www.facebook.com/Womenandtheway",
        "https://www.instagram.com/women_and_the_way"
      ]
    };
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) existingSchema.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, []);

  const handleBooking = async () => {
    setIsLoading(true);
    try {
      window.open(BOOKING_URL, '_blank');
    } catch (error) {
      console.error('Booking error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-white via-white to-amber-50">
        <NavBar />
        
        {/* Hero Section */}
        <section className="relative py-20 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                  Digestion & Gut Health
                </h1>
                <p className="text-xl md:text-2xl text-sage-600 mb-6 font-medium">
                  Rebuild your gut. Reclaim your energy. Trust your belly again.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  Digestive health is the foundation of overall wellbeing. At Women and the Way, we understand that gut health affects everything from energy levels to skin health to emotional balance.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  Whether you're experiencing bloating, constipation, IBS, or simply want to optimize your digestive function, our treatments help restore your gut's natural balance. Our approach combines the wisdom of Traditional Chinese Medicine with modern understanding of digestive health. Many of our clients also benefit from our <a href="/skin-health" className="text-sage-600 hover:text-sage-800 underline transition-colors">skin health services</a> and <a href="/stress-burnout" className="text-sage-600 hover:text-sage-800 underline transition-colors">stress relief treatments</a>.
                </p>
                <Button 
                  size="lg" 
                  className="bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                  onClick={handleBooking}
                  disabled={isLoading}
                >
                  {isLoading ? 'Booking...' : 'Book Now'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <div className="relative">
                <img 
                  src={heroImage} 
                  alt="Digestion and gut health support with Chinese medicine on the Sunshine Coast" 
                  className="rounded-2xl shadow-lg w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Why Gut Health Matters */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6 flex items-center justify-center gap-3">
                <Apple className="w-8 h-8 text-sage-600" />
                Why Gut Health Matters in Chinese Medicine
              </h2>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12">
              <p className="text-lg text-sage-700 mb-6 leading-relaxed">
                In Traditional Chinese Medicine, digestion is ruled by the Spleen and Stomach, which transform food into Qi (energy) and Blood (nourishment). When these organs are weakened by stress, poor diet, or overwork, you might feel bloated, heavy, foggy, or fatigued. Over time, it can affect your hormones, skin, immunity, and mental clarity.
              </p>
              <p className="text-lg text-sage-700 leading-relaxed">
                At Women and the Way, we support digestion from the inside out - strengthening the Spleen, calming the Liver, and working with your body's natural rhythm to restore flow, ease, and trust in your gut again.
              </p>
            </div>
          </div>
        </section>

        {/* What We Treat */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                What We Treat
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Brain className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-xl font-semibold text-sage-800">Bloating, fullness, or sluggish digestion</h3>
                  </div>
                  <p className="text-sage-700">Spleen Qi deficiency, Damp accumulation</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Zap className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-xl font-semibold text-sage-800">Gas, loose stools, or urgency</h3>
                  </div>
                  <p className="text-sage-700">Damp-Heat in the Intestines, Spleen-Liver disharmony</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Coffee className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-xl font-semibold text-sage-800">Fatigue after eating</h3>
                  </div>
                  <p className="text-sage-700">Spleen Yang deficiency, Qi stagnation</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Activity className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-xl font-semibold text-sage-800">Constipation or irregular bowels</h3>
                  </div>
                  <p className="text-sage-700">Qi stagnation, Blood deficiency, Heat in the colon</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Apple className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-xl font-semibold text-sage-800">Food sensitivities & leaky gut</h3>
                  </div>
                  <p className="text-sage-700">Spleen Qi deficiency, Yin deficiency, Damp retention</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Target className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-xl font-semibold text-sage-800">Post-antibiotic or post-viral gut issues</h3>
                  </div>
                  <p className="text-sage-700">Qi and Yin deficiency, lingering Damp-Heat</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* What We Offer */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                What We Offer
              </h2>
              <p className="text-xl text-sage-600 mb-8">
                Digestive Support In-Clinic or Online
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* In-Clinic Care */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Sparkles className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Acupuncture</h3>
                  </div>
                  <p className="text-sage-700 mb-4">To regulate digestion, reduce bloating, and calm stress-induced symptoms</p>
                  <div className="flex items-center gap-3 mb-4">
                    <Leaf className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Chinese Herbal Medicine</h3>
                  </div>
                  <p className="text-sage-700 mb-4">To strengthen the Spleen, clear Damp, and restore digestive harmony</p>
                  <div className="flex items-center gap-3 mb-4">
                    <Users className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Nutrition Guidance</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Personalised food therapy and eating habits for your constitution</p>
                </CardContent>
              </Card>

              {/* Online Sessions */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <MessageCircle className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Telehealth Consultations</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Digestive guidance and support from the comfort of your home</p>
                  <div className="flex items-center gap-3 mb-4">
                    <Leaf className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Herbal Support</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Custom formulas mailed to your door for ongoing digestive support</p>
                  <div className="flex items-center gap-3 mb-4">
                    <BookOpen className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Gut Health Resources</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Nutrition guides, lifestyle tips, and digestive wellness tools</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
              Book Your Session
            </h2>
            <p className="text-xl text-sage-700 mb-8">
              Rebuild your gut. Reclaim your energy. Trust your belly again.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <MapPin className="w-5 h-5 text-sage-600" />
                <a 
                  href="https://maps.google.com/?q=700+Nicklin+Way+Currimundi+QLD" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-sage-800 transition-colors"
                >
                  700 Nicklin Way, Currimundi
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Phone className="w-5 h-5 text-sage-600" />
                <a 
                  href="tel:0415537784" 
                  className="hover:text-sage-800 transition-colors"
                >
                  0415 537 784
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Mail className="w-5 h-5 text-sage-600" />
                <a 
                  href="mailto:womenandtheway@gmail.com" 
                  className="hover:text-sage-800 transition-colors"
                >
                  womenandtheway@gmail.com
                </a>
              </div>
            </div>
            <Button 
              size="lg" 
              className="w-full sm:w-auto text-center break-words bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 mx-auto"
              onClick={handleBooking}
              disabled={isLoading}
            >
              {isLoading ? 'Booking...' : 'Book Your Session'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </section>
        <ScrollDownArrow />
        <ReturnToTop />
      </div>
    </>
  );
};

export default DigestionGutHealthPage; 